import React from 'react';
import { Box } from '@mui/material';
import { mediaUrl } from '../../utils/media';

/** The side of a thumbnail in a list, in pixels: a glimpse, not the content. */
const LIST_TILE = { xs: 72, sm: 96 };

/** The side of a tile on a page of its own, when there are several photos. */
const PAGE_TILE = { xs: 'calc(50% - 4px)', sm: 200 };

/** How tall a photo alone may be on a page of its own. */
const PAGE_SINGLE_MAX_HEIGHT = { xs: 260, sm: 360 };

/**
 * The photos of a post or an event.
 *
 * Kept small on purpose: the words are what a neighbour came to read, and a
 * photo that fills the screen pushes them out of it.
 *
 * - `compact` (the feed, the agenda): a row of small squares, so a list with
 *   photos stays a list rather than a gallery.
 * - On a page of its own: one photo whole at its own proportions, capped in
 *   height; several as a row of squares. A tap opens the large size in full.
 *
 * Each `<img>` keeps the photo's width and height, so the browser knows its
 * proportions before it arrives.
 */
function PhotoGallery({ medias = [], title = '', compact = false }) {
  if (!medias.length) {
    return null;
  }

  const variant = compact ? 'thumb' : 'large';
  const alone = 1 === medias.length && !compact;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {medias.map((media, index) => {
        // A plain <img>: on a MUI Box, width and height would be read as
        // style props and never reach the tag.
        const img = (
          <img
            src={mediaUrl(media.uuid, variant)}
            width={media.width}
            height={media.height}
            loading="lazy"
            decoding="async"
            alt={medias.length > 1 ? `Photo ${index + 1} de « ${title} »` : `Photo de « ${title} »`}
            style={
              alone
                ? { display: 'block', maxWidth: '100%', width: 'auto', height: 'auto' }
                : { display: 'block', width: '100%', height: '100%', objectFit: 'cover' }
            }
          />
        );

        const frame = alone
          ? { maxWidth: '100%', '& img': { maxHeight: PAGE_SINGLE_MAX_HEIGHT } }
          : {
              width: compact ? LIST_TILE : PAGE_TILE,
              aspectRatio: '1',
              flexShrink: 0,
            };

        return (
          <Box
            key={media.uuid}
            sx={{ ...frame, borderRadius: '10px', overflow: 'hidden', bgcolor: 'surface.chip' }}
          >
            {compact ? (
              img
            ) : (
              <a href={mediaUrl(media.uuid, 'large')} target="_blank" rel="noopener noreferrer">
                {img}
              </a>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default PhotoGallery;
